import { Component, Input, OnInit, ElementRef, OnDestroy, AfterViewInit, ViewChild } from '@angular/core';
import { WidgetComponent } from '@widgets/widgets';
import { CourseAutoevaluationsSummary } from '@widgets/models/CourseAutoevaluationsSummary';
import { AutoevaluationsService } from '../../services/autoevaluations.service';
import { UtilsService } from 'core/services/utils.service';

const QUESTION_WIDTH = 26; //%
const QUESTION_HEIGHT = 63; //px

@Component({
  selector: 'lms-autoevaluationCourses',
  templateUrl: './autoevaluationCourses.component.html',
  styleUrls: ['./autoevaluationCourses.component.scss']
})
export class AutoevaluationCoursesComponent implements WidgetComponent, OnInit, AfterViewInit, OnDestroy {
  @Input() public data: any;
  @Input() public disabledHeader: boolean = false;
  @ViewChild('autoevaluation_questions', { static: false }) public questionsBox: ElementRef;
  @ViewChild('autoevaluation_radar_chart', { static: false }) public radarChart: ElementRef;

  public verticalRatio = 1;
  public horizontalRatio = 1;

  public coursesAutoevaluations: CourseAutoevaluationsSummary[];
  public selectedCourse: CourseAutoevaluationsSummary;
  public coursesOptions: any[];
  public isEmpty: boolean = true;
  public clientId: string;

  public radarChartOptions: any = {
    responsive: false,
    plugins: {
      legend: {
        display: false
      }
    },
    elements: {
      line: {
        borderWidth: 1
      }
    },
    scales: {
      r: {
        beginAtZero: 1,
        pointLabels: {
          color: 'rgb(205, 208, 212)'
        },
        ticks: {
          min: 1,
          max: 10,
          stepSize: 1,
          display: false
        }
      }
    }
  };

  public radarChartLabels = [];
  public radarChartQuestions = [];

  public radarChartData = [
    {
      label: 'pre',
      backgroundColor: 'rgba(0, 126, 255, 0.56)',
      borderColor: 'rgb(0, 126, 255)',
      pointBorderColor: 'rgb(0, 126, 255)',
      pointBackgroundColor: 'rgb(0, 126, 255)',
      pointHoverBackgroundColor: 'rgb(0, 126, 255)',
      pointHoverBorderWidth: '2',
      pointBorderWidth: 2,
      pointHoverRadius: 2,
      data: []
    },
    {
      label: 'post',
      backgroundColor: 'rgba(173, 213, 255,0.32)',
      borderColor: 'rgb(173, 213, 255)',
      pointBorderColor: 'rgb(173, 213, 255)',
      pointBackgroundColor: 'rgb(173, 213, 255)',
      pointHoverBackgroundColor: 'rgb(173, 213, 255)',
      pointHoverBorderWidth: '2',
      pointBorderWidth: 2,
      pointHoverRadius: 2,
      data: []
    },
    // hack for background color
    {
      data: Array.from(Array(10)).map(() => 10),
      label: 'background',
      backgroundColor: 'rgba(225, 228, 232, 0.5)',
      borderColor: 'rgb(225, 228, 232)',
      pointRadius: 0
    }
  ];

  public readonly: boolean = false;

  constructor(public autoevaluationsService: AutoevaluationsService) {}

  public ngOnInit(): void {
    this.isEmpty = !!this.data.coursesAutoevaluations['empty'];
    if (!this.isEmpty) {
      this.coursesAutoevaluations = this.data.coursesAutoevaluations.sort(
        (a: CourseAutoevaluationsSummary, b: CourseAutoevaluationsSummary) => a.surveysCount - b.surveysCount
      );
      this.setCoursesOptions();
    }
    const client = this.data.managedAccounts?.length === 1 ? this.data.managedAccounts[0] : null;
    const group = this.data.group;
    this.clientId = group ? group.clientId : client ? client.id : null;

    this.readonly = this.coursesOptions && this.coursesOptions.length === 1 ? true : null;
  }

  public ngAfterViewInit(): void {
    if (!this.isEmpty) {
      setTimeout(() => {
        const boxHeight = this.questionsBox.nativeElement.offsetHeight;
        const boxWidth = this.questionsBox.nativeElement.offsetWidth;
        const chartHeight = this.radarChart.nativeElement.offsetHeight + 10;
        const chartWidth = this.radarChart.nativeElement.offsetWidth + 10;
        this.verticalRatio = chartHeight / boxHeight;
        this.horizontalRatio = chartWidth / boxWidth;
        const firstCourseImprovement = {
          courseId: this.coursesAutoevaluations[0].courseId,
          improvement: this.coursesAutoevaluations[0].improvement
        };
        const maxImprovementCourse = this.coursesAutoevaluations.reduce((currentMaxCourse, course) => {
          const improvement = course.improvement;
          return improvement > currentMaxCourse.improvement
            ? { courseId: course.courseId, improvement }
            : currentMaxCourse;
        }, firstCourseImprovement);
        this.setSelectedCourse(maxImprovementCourse.courseId);
      }, 500);
    }
  }

  public ngOnDestroy(): void {}

  setCoursesOptions() {
    this.coursesOptions = this.coursesAutoevaluations
      .map((course: CourseAutoevaluationsSummary) => ({
        key: course.courseId,
        value: course.gameName
      }))
      .sort((a, b) => (a.value > b.value ? 1 : a.value < b.value ? -1 : 0));
  }

  setSelectedCourse(courseId): void {
    this.selectedCourse = this.coursesAutoevaluations.find(
      (course: CourseAutoevaluationsSummary) => course.courseId === courseId
    );
    this.setQuestions();
  }

  setQuestions() {
    this.autoevaluationsService
      .getGameQuestions(this.selectedCourse.gameId)
      .then((texts: any) => {
        this.setQuestionsText(this.selectedCourse?.questions, texts);
      })
      .catch((e) => {
        this.setQuestionsText([]);
      });
  }

  setQuestionsText(questionsData, questionTexts?): void {
    const questions = questionsData
      ? questionsData.filter((question: any) => questionTexts.contents[questionTexts.baseLang][question.questionId])
      : [];
    const questionsCount = questions.length;
    this.radarChartLabels = Array.from(Array(questionsCount)).map((x, i) => `${i + 1}`);
    this.radarChartData[0].data = questions.map((q) => Math.round(q.avgPre));
    this.radarChartData[1].data = questions.map((q) => Math.round(q.avgPost));
    this.radarChartData[2].data = Array.from(Array(questionsCount)).map(() => 10);
    const userLang = localStorage.getItem('backofficelanguage');
    const baseLang = questionTexts ? questionTexts.baseLang : null;

    this.radarChartQuestions = questions.map((q: any, i) => {
      const degrees = 360 - (360 / questionsCount) * i;
      const rad = (degrees * Math.PI) / 180;
      const topOrDown = [0, Math.floor(questionsCount / 2), Math.ceil(questionsCount / 2)].includes(i);
      const isCentered = [0, 180, 360].includes(degrees);
      const rightHalf = degrees > 180 && degrees < 360;
      const deltaLeft = isCentered ? QUESTION_WIDTH / 2 : rightHalf ? -1 : QUESTION_WIDTH + 1;
      const deltaTop = i === 0 ? QUESTION_HEIGHT : topOrDown ? 0 : QUESTION_HEIGHT / 2;
      const left = 50 - Math.sin(rad) * 50 * this.horizontalRatio;
      const top = 50 - Math.cos(rad) * 50 * this.verticalRatio;

      const questionText =
        UtilsService.getDeepValue(questionTexts, `contents.${userLang}.${q.questionId}`) ||
        UtilsService.getDeepValue(questionTexts, `contents.${baseLang}.${q.questionId}`);

      return {
        text: questionText ? `${i + 1}. ${questionText}` : '',
        style: {
          left: `calc(${left}% - ${deltaLeft}%)`,
          top: `calc(${top}% - ${deltaTop}px)`
        }
      };
    });
  }
}
